import React, { useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { CalendarDays, Sparkles, Bell, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { statusColors, complianceStatusColors, priorityColors } from "./ComplianceScoreRing";

export function ObligationRow({ ob, contractMap, users, onUpdate, onDelete }) {
  const days = ob.due_date ? Math.ceil((new Date(ob.due_date) - new Date()) / 86400000) : null;
  const [showReminder, setShowReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState(ob.reminder_date || "");

  const handleSetReminder = async () => {
    try {
      await apiClient.post("/compliance/set-reminder", { item_type: "obligation", item_id: ob.id, reminder_date: reminderDate });
      toast.success("Reminder set"); setShowReminder(false); onUpdate();
    } catch { toast.error("Failed to set reminder"); }
  };

  return (
    <div className="flex flex-col gap-2 p-4 bg-white dark:bg-slate-900 border rounded-sm" data-testid={`obligation-${ob.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="font-medium text-sm truncate">{ob.title}</p>
            <Badge className={`text-xs rounded-sm ${priorityColors[ob.priority]}`}>{ob.priority}</Badge>
            <Badge className={`text-xs rounded-sm ${statusColors[ob.status]}`}>{ob.status.replace("_", " ")}</Badge>
            <Badge className={`text-xs rounded-sm ${complianceStatusColors[ob.compliance_status || "pending_review"]}`}>
              {(ob.compliance_status || "pending_review").replace("_", " ")}
            </Badge>
            {ob.source === "ai_extracted" && <Badge className="text-xs rounded-sm bg-violet-100 text-violet-700"><Sparkles size={10} className="mr-1" /> AI</Badge>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span>{contractMap[ob.contract_id] || ob.contract_id?.substring(0, 8)}</span>
            <span className="flex items-center gap-1">
              <CalendarDays size={12} /> {ob.due_date}
              {days !== null && ob.status !== "completed" && (
                <span className={days < 0 ? "text-red-600 font-medium" : days <= 7 ? "text-amber-600" : "text-slate-500"}>
                  ({days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`})
                </span>
              )}
            </span>
            {ob.assigned_to_name && <span>Assigned: {ob.assigned_to_name}</span>}
            <span className="capitalize">{ob.responsible_party}</span>
            {ob.reminder_date && <span className="flex items-center gap-1 text-amber-600"><Bell size={10} /> Reminder: {ob.reminder_date}</span>}
          </div>
          {ob.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{ob.description}</p>}
        </div>
        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-500" onClick={() => setShowReminder(!showReminder)} data-testid={`ob-reminder-btn-${ob.id}`}><Bell size={14} /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete(ob.id)} data-testid={`ob-delete-btn-${ob.id}`}><Trash2 size={14} /></Button>
        </div>
      </div>
      {showReminder && (
        <div className="flex items-center gap-2 pt-2 border-t">
          <Label className="text-xs whitespace-nowrap">Reminder Date:</Label>
          <Input type="date" className="h-8 rounded-sm text-xs w-40" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} />
          <Button size="sm" className="h-8 rounded-sm text-xs" onClick={handleSetReminder} disabled={!reminderDate}>Set</Button>
        </div>
      )}
    </div>
  );
}
